/**
 * AIChatWidget.jsx — Kontekstuell juridisk AI-assistent for B2B-moduler
 * 
 * Flytende knapp nederst til høyre. Privacy-First: Sesjon slettes ved lukking/logout.
 * Kontekst-mapping basert på URL → system-prompt i backend.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  MessageCircle, X, Send, Loader2, Sparkles, AlertCircle, Bot
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kontekst-mapping: URL-segment → modul-nøkkel
const ROUTE_TO_MODULE = {
  'husleierett-naering': { key: 'husleierett-naering', label: 'Næringseiendom', color: '#5B8BA0' },
  'gdpr':               { key: 'gdpr',                label: 'GDPR / Personvern', color: '#6366f1' },
  'arbeidsrett-bedrift': { key: 'arbeidsrett-bedrift', label: 'Arbeidsrett', color: '#f59e0b' },
  'dashboard/barnehage': { key: 'barnehage',           label: 'Barnehage', color: '#22c55e' },
  'barnehage':          { key: 'barnehage',           label: 'Barnehage', color: '#22c55e' },
  'skole':              { key: 'skole',               label: 'Skole', color: '#3b82f6' },
  'barnevern':          { key: 'barnevern-master',    label: 'Barnevern', color: '#ef4444' },
  'kontraktsbibliotek': { key: 'kontraktsbibliotek',  label: 'Kontrakter', color: '#B87333' },
};

// Velkomstmeldinger per modul
const WELCOME_MESSAGES = {
  'barnehage': 'Velkommen til RettVerks barnehage-assistent. Jeg er din ekspert på barnehageloven, rammeplanen og tilhørende forskrifter. Hva kan jeg hjelpe deg med i dag?',
  'husleierett-naering': 'Velkommen til RettVerks næringsleie-assistent. Jeg er spesialist på husleieloven, MVA ved utleie og kommersiell eiendomsforvaltning. Hva kan jeg hjelpe deg med?',
  'gdpr': 'Velkommen til RettVerks personvernrådgiver. Jeg er din DPO-ekspert på GDPR, personopplysningsloven og Datatilsynets retningslinjer. Hva kan jeg hjelpe deg med?',
  'arbeidsrett-bedrift': 'Velkommen til RettVerks arbeidsrettsassistent. Jeg er spesialist på arbeidsmiljøloven og personalforvaltning. Hva kan jeg hjelpe deg med?',
  'skole': 'Velkommen til RettVerks skolerett-assistent. Jeg er ekspert på opplæringsloven og elevrettigheter. Hva kan jeg hjelpe deg med?',
  'barnevern-master': 'Velkommen til RettVerks barnevern-assistent. Jeg er ekspert på barnevernsloven og forvaltningsrett. Hva kan jeg hjelpe deg med?',
  'kontraktsbibliotek': 'Velkommen til RettVerks kontraktsassistent. Jeg er ekspert på norsk kontraktsrett og avtalerett. Hva kan jeg hjelpe deg med?',
};

function detectModuleContext(pathname) {
  for (const [segment, config] of Object.entries(ROUTE_TO_MODULE)) {
    if (pathname.includes(segment)) return config;
  }
  return { key: null, label: 'Generell juridisk', color: '#8899A8' };
}

// ═══════════════════════════════════════════════════════════════
// CHAT MESSAGE BUBBLE
// ═══════════════════════════════════════════════════════════════
const ChatBubble = React.memo(function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      data-testid={`chat-msg-${msg.role}`}
    >
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-[#B87333]/20 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
          <Bot className="w-3.5 h-3.5 text-[#B87333]" />
        </div>
      )}
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-[#5B8BA0] text-white rounded-br-md'
            : 'bg-[#1B2631] border border-[#2C3E50] text-[#CCD6DD] rounded-bl-md'
        }`}
      >
        <div className="whitespace-pre-wrap">{msg.text}</div>
        {msg.model && (
          <div className="mt-1.5 text-[10px] opacity-50 flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            {msg.model}
          </div>
        )}
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════
// MAIN WIDGET
// ═══════════════════════════════════════════════════════════════
export default function AIChatWidget() {
  const { token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const moduleCtx = useMemo(() => detectModuleContext(location.pathname), [location.pathname]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Scroll til bunnen ved nye meldinger
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fokuser input når chatten åpnes
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Rydd opp sesjon ved lukking
  const closeChat = useCallback(async () => {
    if (sessionId) {
      try { await axios.delete(`${API}/ai-chat/session/${sessionId}`, { headers }); } catch {}
    }
    setOpen(false);
    setMessages([]);
    setSessionId(null);
    setInput('');
    setError(null);
  }, [sessionId, headers]);

  // Opprett ny sesjon ved åpning med velkomstmelding
  const openChat = useCallback(async () => {
    setOpen(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/ai-chat/new-session`, {}, { headers });
      setSessionId(res.data.session_id);
      // Modulspesifikk velkomstmelding
      const welcomeText = WELCOME_MESSAGES[moduleCtx.key];
      if (welcomeText) {
        setMessages([{ role: 'assistant', text: welcomeText }]);
      }
    } catch {
      setError('Kunne ikke starte chat-sesjon');
    }
  }, [headers, moduleCtx.key]);

  // Send melding
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput('');
    setError(null);
    setMessages(prev => [...prev, { role: 'user', text }]);
    setSending(true);

    try {
      const res = await axios.post(`${API}/ai-chat/message`, {
        session_id: sessionId,
        message: text,
        module_context: moduleCtx.key,
      }, { headers });

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        model: res.data.model_used,
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Noe gikk galt. Prøv igjen.';
      setError(detail);
    }
    setSending(false);
  }, [input, sending, sessionId, moduleCtx.key, headers]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // Ikke vis widget uten token
  if (!token) return null;

  return (
    <>
      {/* Flytende knapp */}
      {!open && (
        <button
          onClick={openChat}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-[#B87333] text-white shadow-lg shadow-[#B87333]/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          data-testid="ai-chat-toggle"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat-vindu */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[400px] h-[560px] bg-[#0F1419] border border-[#2C3E50] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2C3E50] bg-[#1B2631]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#B87333]/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#B87333]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">RettVerk AI</h3>
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: moduleCtx.color }}
                  />
                  <span className="text-[10px] text-[#8899A8]">{moduleCtx.label}</span>
                </div>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="p-1.5 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white transition-colors"
              data-testid="ai-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="w-12 h-12 rounded-xl bg-[#B87333]/10 flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-[#B87333]" />
                </div>
                <p className="text-sm text-white font-medium mb-1">Juridisk AI-assistent</p>
                <p className="text-xs text-[#8899A8] leading-relaxed">
                  Spesialisert for <span style={{ color: moduleCtx.color }} className="font-medium">{moduleCtx.label}</span>.
                  Still spørsmål, lim inn kontraktstekst for analyse, eller be om forslag til maler.
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <ChatBubble key={i} msg={msg} />
            ))}

            {sending && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-[#B87333]/20 flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5 text-[#B87333]" />
                </div>
                <div className="px-3.5 py-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin text-[#B87333]" />
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg mb-3">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-xs text-red-300">{error}</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-[#2C3E50] bg-[#1B2631]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Skriv en melding..."
                rows={1}
                className="flex-1 px-3 py-2 bg-[#0F1419] border border-[#2C3E50] rounded-xl text-sm text-white placeholder-[#4a5568] resize-none focus:outline-none focus:border-[#B87333]/50 max-h-24"
                disabled={sending || !sessionId}
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim() || !sessionId}
                className="p-2.5 rounded-xl bg-[#B87333] text-white disabled:opacity-40 hover:bg-[#a0612b] transition-colors flex-shrink-0"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[9px] text-[#4a5568] mt-1.5 px-1">
              AI kan gjøre feil. Verifiser alltid juridisk informasjon.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
